import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CameraAlt,
  PlayArrow,
  Stop,
  Settings,
  OpenInNew,
  Computer,
} from '@mui/icons-material';
import { useBrandColors } from '../hooks/useBrandColors';
import { aiTrackingService } from '../services/aiTrackingService';
import { apiService } from '../services/api';
import timezoneService from '../services/timezoneService';
import { DateService } from '../utils/dateService';
import { calculateTotalTime, formatDuration as formatDurationUtil } from '../utils/durationCalculator';
import { format } from 'date-fns-tz';

interface AITrackingStatus {
  isRunning: boolean;
  lastScreenshot: Date | null;
  config: any;
}

interface TrackingSession {
  date: string;
  totalTime: number; // in minutes
  sessions: number;
  startTime?: string;
  endTime?: string;
}

const AITrackingWidget: React.FC = () => {
  const brandColors = useBrandColors();
  const [trackingStatus, setTrackingStatus] = useState<AITrackingStatus>({
    isRunning: false,
    lastScreenshot: null,
    config: null
  });
  const [trackingSessions, setTrackingSessions] = useState<TrackingSession[]>([]);
  const [viewMode] = useState<'daily' | 'weekly'>('daily');
  const [loading, setLoading] = useState(false);
  const [weekAnalytics, setWeekAnalytics] = useState<any>(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  useEffect(() => {
    // Subscribe to tracking status updates
    const unsubscribe = aiTrackingService.subscribe((status) => {
      setTrackingStatus(status);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    loadTrackingSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const loadTrackingSessions = async () => {
    try {
      setLoading(true);
      const sessions = await getTrackingSessionsFromScreenshots();
      setTrackingSessions(sessions);
    } catch (error) {
      console.error('Failed to load tracking sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrackingSessionsFromScreenshots = async (): Promise<TrackingSession[]> => {
    try {
      // NEW ARCHITECTURE: Frontend calculates everything using gap detection
      
      // 1. Get user's screenshot interval from AI settings
      const settings = await apiService.getAISettings();
      const interval = settings.data?.screenshotInterval || 5; // Default 5 if not set
      
      console.log(`⚙️ AITrackingWidget: Using screenshot interval ${interval} minutes`);
      
      // 2. Get screenshots for last 7 days using smart date filtering
      const todayUserTZ = timezoneService.getCurrentUserDate(); // YYYY-MM-DD
      
      // Calculate week range
      // eslint-disable-next-line no-restricted-syntax
      const weekAgoDate = new Date();
      weekAgoDate.setDate(weekAgoDate.getDate() - 7);
      const weekAgoKey = DateService.toStandardDate(weekAgoDate);
      
      // Convert today to UTC range to cover full local day
      const { startUTC: todayStartUTC, endUTC: todayEndUTC } = DateService.userDateToUTCRange(todayUserTZ);
      
      // Backend does coarse UTC filtering (performance optimization)
      const screenshotsResponse = await apiService.getScreenshots({
        startDate: weekAgoKey,
        endDate: todayEndUTC, // Use UTC end time instead of YYYY-MM-DD
        limit: 999
      } as any);
      
      const allScreenshots = screenshotsResponse.data;

      console.log(`📸 AITrackingWidget: Got ${allScreenshots.length} screenshots from backend (last 7 days)`);

      // 3. Filter for today in user's timezone (frontend exact filtering)

      const todayScreenshots = allScreenshots.filter((screenshot: any) => {
        const zonedDate = timezoneService.convertUTCToUserTime(screenshot.timestamp);
        const dateKey = format(zonedDate, 'yyyy-MM-dd');
        return dateKey === todayUserTZ;
      });
      
      console.log(`📅 AITrackingWidget: Filtered to ${todayScreenshots.length} screenshots for today (${todayUserTZ})`);
      
      // 4. Calculate total time using smart gap detection
      const totalTimeToday = calculateTotalTime(todayScreenshots, interval);
      
      // 5. All screenshots already filtered to last 7 days by backend
      // Just use them directly for week calculation
      const weekScreenshots = allScreenshots;
      
      const totalTimeWeek = calculateTotalTime(weekScreenshots, interval);
      
      console.log(`📊 AITrackingWidget: Today ${totalTimeToday}min, Week ${totalTimeWeek}min`);
      
      // Store week analytics for UI
      setWeekAnalytics({
        totalHours: totalTimeWeek / 60,
        screenshotCount: weekScreenshots.length
      });
      
      // Create session data
      const todaySession: TrackingSession = {
        date: todayUserTZ,
        totalTime: totalTimeToday,
        sessions: todayScreenshots.length,
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString()
      };
      
      return [todaySession];
        
    } catch (error) {
      console.error('Error getting tracking sessions:', error);
      return [];
    }
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getTotalWeekTime = () => {
    return trackingSessions.reduce((total: number, session: TrackingSession) => total + session.totalTime, 0);
  };

  const handleStartTracking = async () => {
    try {
      // Default config for quick start
      const startConfig = {
        enabled: true,
        screenshotInterval: 5, // 5 minutes
        autoDetection: true,
        projectMatching: true,
        confidenceThreshold: 0.7,
        saveScreenshots: 'server' as const,
        localSavePath: '',
        analysisDepth: 'detailed' as const,
      };

      // Save settings to backend first (same as AISettings)
      const settings = {
        aiTrackingEnabled: true,
        screenshotsEnabled: true,
        screenshotInterval: startConfig.screenshotInterval,
        saveScreenshots: startConfig.saveScreenshots,
        localSavePath: startConfig.localSavePath,
        autoProjectDetection: startConfig.autoDetection,
        smartProjectMatching: startConfig.projectMatching,
        analysisDepth: startConfig.analysisDepth,
        confidenceThreshold: Math.round(startConfig.confidenceThreshold * 100),
      };

      // Save to backend first
      await apiService.updateAISettings(settings);

      // Then start AI tracking service
      await aiTrackingService.start(startConfig);
      
      console.log('AI tracking started successfully from widget');
    } catch (error) {
      console.error('Failed to start AI tracking:', error);
    }
  };

  const handleStopTracking = async () => {
    try {
      // Stop AI tracking service
      aiTrackingService.stop();
      
      // Update backend to reflect stopped state
      const settings = {
        aiTrackingEnabled: false,
        screenshotsEnabled: false,
      };
      
      await apiService.updateAISettings(settings);
      
      console.log('AI tracking stopped successfully from widget');
    } catch (error) {
      console.error('Failed to stop AI tracking:', error);
    }
  };

  const handlePopOut = () => {
    const width = 360;
    const height = 350; // Increased height per user request for more breathing room
    const left = 0; // Position at top-left corner
    const top = 0; // Position at top-left corner
    
    window.open(
      '/widget', 
      'TrackerWidget', 
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no`
    );
  };

  const formatLastScreenshot = (date: Date | null) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Check if running in widget mode (pop-out)
  const isWidgetMode = window.location.pathname === '/widget';

  return (
    <Card sx={{ 
      width: '100%', 
      borderRadius: isWidgetMode ? 0 : 2,
      boxShadow: isWidgetMode ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
      mb: isWidgetMode ? 0 : 2,
      height: isWidgetMode ? '100%' : '200px', 
      display: 'flex',
      flexDirection: 'column'
    }}>
      <CardContent sx={{ 
        py: 2, 
        px: 3, 
        '&:last-child': { pb: 2 },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Main horizontal strip */}
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          
          {/* Left section: AI Tracking status */}
          <Box display="flex" alignItems="center" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <CameraAlt sx={{ fontSize: 20, color: brandColors.primary }} />
              <Typography variant="subtitle1" fontWeight="600">
                AI Tracking
              </Typography>
              <Chip 
                label={trackingStatus.isRunning ? 'Active' : 'Inactive'}
                color={trackingStatus.isRunning ? 'success' : 'default'}
                size="small"
                sx={{ fontSize: '11px', height: 20 }}
              />
            </Box>
            
            <Divider orientation="vertical" flexItem sx={{ height: 30 }} />
            
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="textSecondary">
                Last: {formatLastScreenshot(trackingStatus.lastScreenshot)}
              </Typography>
              {trackingStatus.config?.screenshotInterval && (
                <>
                  <Typography variant="body2" color="textSecondary">•</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {trackingStatus.config.screenshotInterval}min interval
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Center section: Today's tracking stats */}
          {trackingSessions.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ height: 30 }} />
              <Box display="flex" alignItems="center" gap={3}>
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary" display="block">
                    Today
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {formatTime(trackingSessions[0]?.totalTime || 0)}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary" display="block">
                    This Week
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatTime(Math.round((weekAnalytics?.totalHours || 0) * 60))}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary" display="block">
                    Screenshots
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {trackingSessions[0]?.sessions || 0}
                  </Typography>
                </Box>
                
                {/* Mini progress bar for today */}
                <Box sx={{ width: 80 }}>
                  <Typography variant="caption" color="textSecondary" display="block" textAlign="center">
                    Progress
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((trackingSessions[0]?.totalTime || 0) / (8 * 60) * 100, 100)}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: '#f0f0f0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: trackingSessions[0]?.totalTime >= 8 * 60 ? '#4caf50' : '#2196f3'
                      }
                    }}
                  />
                  <Typography variant="caption" color="textSecondary" display="block" textAlign="center">
                    {Math.round((trackingSessions[0]?.totalTime || 0) / (8 * 60) * 100)}%
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Right section: Controls */}
          <Box display="flex" alignItems="center" gap={1}>
            {/* Pop-out button (hidden if already in widget mode) */}
            {window.location.pathname !== '/widget' && (
              <Tooltip title="Open as Widget">
                <IconButton onClick={handlePopOut} size="small" sx={{ color: 'text.secondary' }}>
                  <OpenInNew sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Action buttons */}
            <Box display="flex" gap={1}>
              {!trackingStatus.isRunning ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrow sx={{ fontSize: 16 }} />}
                  onClick={handleStartTracking}
                  size="small"
                  sx={{ fontSize: '12px', py: 0.5, px: 1.5 }}
                >
                  Start
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop sx={{ fontSize: 16 }} />}
                  onClick={handleStopTracking}
                  size="small"
                  sx={{ fontSize: '12px', py: 0.5, px: 1.5 }}
                >
                  Stop
                </Button>
              )}
              
              <Button
                variant="outlined"
                startIcon={<Settings sx={{ fontSize: 16 }} />}
                size="small"
                onClick={() => {
                  // Navigate to AI Settings tab
                  const event = new CustomEvent('navigate-to-tab', { detail: 'ai-settings' });
                  window.dispatchEvent(event);
                }}
                sx={{ fontSize: '12px', py: 0.5, px: 1.5 }}
              >
                Settings
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Computer sx={{ fontSize: 16 }} />}
                size="small"
                onClick={() => setDownloadDialogOpen(true)}
                sx={{ fontSize: '12px', py: 0.5, px: 1.5, borderColor: '#00bcd4', color: '#00bcd4', '&:hover': { borderColor: '#0097a7', backgroundColor: 'rgba(0,188,212,0.04)' } }}
              >
                Desktop App
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Status message strip */}
        <Box mt={1.5}>
          {trackingStatus.isRunning ? (
            <Box 
              display="flex" 
              alignItems="center" 
              gap={1} 
              p={1} 
              sx={{ 
                backgroundColor: '#e8f5e8', 
                borderRadius: 1,
                border: '1px solid #c8e6c9'
              }}
            >
              <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                🟢 AI tracking active - Screenshots being captured and analyzed automatically
              </Typography>
              {loading && <LinearProgress sx={{ width: 60, height: 2 }} />}
            </Box>
          ) : (trackingSessions[0]?.sessions || 0) > 0 ? (
            // Desktop app is sending screenshots
            <Box 
              display="flex" 
              alignItems="center" 
              gap={1} 
              p={1} 
              sx={{ 
                backgroundColor: '#e3f2fd', 
                borderRadius: 1,
                border: '1px solid #90caf9'
              }}
            >
              <Typography variant="caption" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                🖥️ Desktop app active - {trackingSessions[0]?.sessions} screenshots synced today
              </Typography>
            </Box>
          ) : (
            <Box 
              display="flex" 
              alignItems="center" 
              gap={1} 
              p={1} 
              sx={{ 
                backgroundColor: '#fff3e0', 
                borderRadius: 1,
                border: '1px solid #ffcc02'
              }}
            >
              <Typography variant="caption" color="warning.main">
                ⚠️ No tracking active - Use the Desktop App or click "Start" for web tracking
              </Typography>
            </Box>
          )}
        </Box>

      </CardContent>
      
      {/* Desktop App Download Dialog */}
      <Dialog open={downloadDialogOpen} onClose={() => setDownloadDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Computer sx={{ color: '#00bcd4' }} />
          Download Desktop App
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Track your time directly from your desktop with automatic screenshots. The app runs in your menu bar.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => window.open('https://pub-93034ca048324f38ad50807a7d67c5c6.r2.dev/TrackerV2-1.0.0-arm64.dmg', '_blank')}
              sx={{ backgroundColor: '#555', '&:hover': { backgroundColor: '#333' } }}
            >
              🍎 Download for Mac (Apple Silicon)
            </Button>
            
            <Button
              variant="contained"
              fullWidth
              onClick={() => window.open('https://pub-93034ca048324f38ad50807a7d67c5c6.r2.dev/TrackerV2%20Setup%201.0.0.exe', '_blank')}
              sx={{ backgroundColor: '#0078D4', '&:hover': { backgroundColor: '#005a9e' } }}
            >
              🪟 Download for Windows
            </Button>
          </Box>
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            Version 1.0.0 • macOS 10.13+ or Windows 10+
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AITrackingWidget;
